import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import { geoMercator, geoCentroid } from "d3-geo";
import axios from "axios";

// ------------------------------
// Helpers
// ------------------------------
const stateCodeMap = {
  AC: "12", AL: "27", AP: "16", AM: "13", BA: "29",
  CE: "23", DF: "53", ES: "32", GO: "52", MA: "21",
  MT: "51", MS: "50", MG: "31", PA: "15", PB: "25",
  PR: "41", PE: "26", PI: "22", RJ: "33", RN: "24",
  RS: "43", RO: "11", RR: "14", SC: "42", SP: "35",
  SE: "28", TO: "17",
};

// ResizeObserver (melhor que window.resize)
function useResizeObserver(ref) {
  const [rect, setRect] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const r = entries?.[0]?.contentRect;
      if (!r) return;
      setRect({ width: r.width, height: r.height });
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return rect;
}

// ------------------------------
// Component
// ------------------------------
const StateMunicipalitiesMap = ({ stateSigla, year, isMobile = false }) => {
  const navigate = useNavigate();
  const [municipalitiesData, setMunicipalitiesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geoData, setGeoData] = useState(null);

  const [viewMode, setViewMode] = useState("population"); // export | import | balance | population | pib
  const [searchTerm, setSearchTerm] = useState("");
  const [popData, setPopData] = useState([]);
  const [pibData, setPibData] = useState([]);

  const [hoveredMunicipality, setHoveredMunicipality] = useState(null);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);

  const [tooltip, setTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const [products, setProducts] = useState({});

  const mapStageRef = useRef(null);
  const mapViewportRef = useRef(null);
  const stageRect = useResizeObserver(mapStageRef);

  // GeoJSON - busca do PostgreSQL via API
  const stateUF = (stateSigla || "").toUpperCase();
  const stateCode = stateCodeMap[stateUF] || null;

  // Carrega GeoJSON usando axios (que já tem baseURL configurado)
  useEffect(() => {
    if (!stateCode) return;
    let alive = true;

    (async () => {
      try {
        const res = await axios.get(`/geodata/${stateCode}`);
        if (alive) setGeoData(res.data);
      } catch (err) {
        console.error("Erro ao carregar GeoJSON:", err);
        if (alive) setGeoData(null);
      }
    })();

    return () => {
      alive = false;
    };
  }, [stateCode]);

  // Carrega dados dos municípios
  useEffect(() => {
    if (!stateUF || !year) return;
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/comex/state-municipalities/${year}/${stateUF}`);
        if (!alive) return;

        if (res.data?.success) setMunicipalitiesData(res.data.data || []);
        else setMunicipalitiesData([]);
      } catch (error) {
        console.error("Erro ao carregar dados dos municípios:", error);
        if (alive) setMunicipalitiesData([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [stateUF, year]);

  // Carrega dados populacionais e PIB do estado
  useEffect(() => {
    if (!stateUF) return;
    let alive = true;
    (async () => {
      try {
        const [popRes, pibRes] = await Promise.all([
          axios.get(`/populacao/estado/${stateUF}`),
          axios.get(`/pib/estado/${stateUF}`, { params: { ano: 2021 } }).catch(() => null),
        ]);
        if (!alive) return;
        if (popRes.data?.success) setPopData(popRes.data.data?.municipios || []);
        if (pibRes?.data?.success) setPibData(pibRes.data.data || []);
      } catch (err) {
        console.error("Erro ao carregar população/PIB:", err);
      }
    })();
    return () => { alive = false; };
  }, [stateUF]);

  // Mapa de população por código IBGE (7 dígitos)
  const popMap = useMemo(() => {
    const m = {};
    popData.forEach((p) => { m[p.codigoIbge] = p; });
    return m;
  }, [popData]);

  // Mapa de PIB por código IBGE
  const pibMunMap = useMemo(() => {
    const m = {};
    pibData.forEach((p) => { m[p.codigoIbge] = p; });
    return m;
  }, [pibData]);

  // Escala de cores para PIB
  const { pibColorScale: pibScale, pibMaxVal } = useMemo(() => {
    if (pibData.length === 0) return { pibColorScale: null, pibMaxVal: 0 };
    const vals = pibData.map(p => p.pibTotal).filter(Boolean);
    const maxVal = Math.max(...vals, 1);
    return {
      pibColorScale: scaleLinear()
        .domain([0, maxVal * 0.05, maxVal * 0.25, maxVal])
        .range(["#ecfdf5", "#34d399", "#059669", "#064e3b"]),
      pibMaxVal: maxVal,
    };
  }, [pibData]);

  const formatBRL = useCallback((val) => {
    if (val >= 1e9) return `R$ ${(val / 1e9).toFixed(1)} tri`;
    if (val >= 1e6) return `R$ ${(val / 1e6).toFixed(1)} bi`;
    if (val >= 1e3) return `R$ ${(val / 1e3).toFixed(1)} mi`;
    return `R$ ${val.toFixed(0)} mil`;
  }, []);

  // Escala de cores para população
  const { popColorScale: popScale, popMaxVal } = useMemo(() => {
    if (popData.length === 0) return { popColorScale: null, popMaxVal: 0 };
    const pops = popData.map(p => p.populacao);
    const maxVal = Math.max(...pops, 1);
    return {
      popColorScale: scaleLinear()
        .domain([0, maxVal * 0.05, maxVal * 0.25, maxVal])
        .range(["#faf5ff", "#c084fc", "#7c3aed", "#4c1d95"]),
      popMaxVal: maxVal,
    };
  }, [popData]);

  const formatPop = useCallback((val) => new Intl.NumberFormat("pt-BR").format(val), []);

  // Formatação pt-BR
  const formatUSDBR = useCallback((val) => {
    if (val === undefined || val === null || isNaN(val)) return "US$ 0";
    const numVal = parseFloat(val);
    if (isNaN(numVal) || numVal === 0) return "US$ 0";

    const absVal = Math.abs(numVal);
    if (absVal >= 1_000_000_000) return `US$ ${(numVal / 1_000_000_000).toFixed(1).replace(".", ",")} bi`;
    if (absVal >= 1_000_000) return `US$ ${(numVal / 1_000_000).toFixed(1).replace(".", ",")} mi`;
    if (absVal >= 1_000) return `US$ ${(numVal / 1_000).toFixed(1).replace(".", ",")} mil`;

    return `US$ ${numVal.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }, []);

  // KPIs
  const kpis = useMemo(() => {
    if (!municipalitiesData?.length) {
      return { totalExport: 0, totalImport: 0, totalBalance: 0, municipalitiesCount: 0 };
    }
    const totalExport = municipalitiesData.reduce((sum, m) => sum + (parseFloat(m.export) || 0), 0);
    const totalImport = municipalitiesData.reduce((sum, m) => sum + (parseFloat(m.import) || 0), 0);
    return {
      totalExport,
      totalImport,
      totalBalance: totalExport - totalImport,
      municipalitiesCount: municipalitiesData.length,
    };
  }, [municipalitiesData]);

  // Escala de cores (domínio por modo)
  const { colorScale, minValue, maxValue } = useMemo(() => {
    if (!municipalitiesData?.length) {
      return {
        colorScale: scaleLinear().domain([-1, 0, 1]).range(["#c0392b", "#ecf0f1", "#2980b9"]),
        minValue: -1,
        maxValue: 1,
      };
    }

    const values =
      viewMode === "export"
        ? municipalitiesData.map((m) => parseFloat(m.export) || 0)
        : viewMode === "import"
          ? municipalitiesData.map((m) => parseFloat(m.import) || 0)
          : municipalitiesData.map((m) => parseFloat(m.balance) || 0);

    const min = Math.min(...values);
    const max = Math.max(...values);

    if (viewMode === "balance") {
      const absMax = Math.max(Math.abs(min), Math.abs(max));
      const domainMin = min < 0 ? -absMax : 0;
      const domainMax = max > 0 ? absMax : 0;

      return {
        colorScale: scaleLinear().domain([domainMin, 0, domainMax]).range(["#c0392b", "#ecf0f1", "#2980b9"]),
        minValue: domainMin,
        maxValue: domainMax,
      };
    }

    return {
      colorScale: scaleLinear().domain([0, max || 1]).range(["#ecf0f1", "#2980b9"]),
      minValue: 0,
      maxValue: max || 1,
    };
  }, [municipalitiesData, viewMode]);

  // ------------------------------
  // ✅ Calcula bbox e aspect do estado
  // ------------------------------
  const geoBBox = useMemo(() => {
    if (!geoData?.features?.length) return null;

    let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;

    const walk = (coords) => {
      if (!Array.isArray(coords)) return;
      if (typeof coords[0] === "number") {
        const [lon, lat] = coords;
        if (Number.isFinite(lon) && Number.isFinite(lat)) {
          minLon = Math.min(minLon, lon);
          maxLon = Math.max(maxLon, lon);
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);
        }
        return;
      }
      coords.forEach(walk);
    };

    geoData.features.forEach((f) => walk(f?.geometry?.coordinates));

    if (!Number.isFinite(minLon) || !Number.isFinite(minLat)) return null;

    return { minLon, maxLon, minLat, maxLat };
  }, [geoData]);

  const geoAspect = useMemo(() => {
    if (!geoBBox) return 1.6;

    const { minLon, maxLon, minLat, maxLat } = geoBBox;

    const lonRange = Math.max(1e-6, maxLon - minLon);
    const latRange = Math.max(1e-6, maxLat - minLat);
    const centerLat = (minLat + maxLat) / 2;

    // ajuste mercator aproximado
    const adjLon = lonRange * Math.cos((centerLat * Math.PI) / 180);
    const aspect = adjLon / latRange;

    return Math.max(0.75, Math.min(3.6, aspect));
  }, [geoBBox]);

  // ------------------------------
  // ✅ Box do mapa: width + height dinâmicos (resolve seu print)
  // ------------------------------
  const { mapW, mapH } = useMemo(() => {
    const availableW = stageRect.width || (isMobile ? 360 : 860);

    // limites bons p/ UX
    const maxH = isMobile ? 520 : 640;
    const minH = isMobile ? 340 : 420;

    let w = availableW;
    let h = w / geoAspect;

    // se ficou alto demais, limita altura e recalcula largura
    if (h > maxH) {
      h = maxH;
      w = h * geoAspect;
    }

    // se ficou baixo demais (estado “baixinho”), impõe altura mínima e reduz largura
    if (h < minH) {
      h = minH;
      w = h * geoAspect;
    }

    // nunca estoura a coluna
    if (w > availableW) {
      w = availableW;
      h = w / geoAspect;
    }

    // limites finais (evita box minúsculo)
    const minW = isMobile ? 320 : 560;
    if (w < minW) {
      w = Math.min(availableW, minW);
      h = w / geoAspect;
    }

    return { mapW: Math.round(w), mapH: Math.round(h) };
  }, [stageRect.width, isMobile, geoAspect]);


  // ✅ Geo para "fit" sem outliers (remove ilhas distantes do cluster principal)
  const fitGeoForProjection = useMemo(() => {
    if (!geoData?.features?.length) return null;

    const cents = geoData.features
      .map((f) => geoCentroid(f))
      .map(([x, y]) => [Number(x), Number(y)])
      .filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y));

    if (cents.length < 8) return geoData;

    const mean = [
      cents.reduce((s, c) => s + c[0], 0) / cents.length,
      cents.reduce((s, c) => s + c[1], 0) / cents.length,
    ];

    const dists = cents.map(([x, y]) => Math.hypot(x - mean[0], y - mean[1]));
    const sorted = [...dists].sort((a, b) => a - b);

    const q = (p) => sorted[Math.floor((sorted.length - 1) * p)];
    const q25 = q(0.25);
    const q75 = q(0.75);
    const iqr = Math.max(1e-9, q75 - q25);

    // Regra IQR para cortar outliers “muito longe” (ilhas)
    const cutoff = q75 + 3 * iqr;

    const kept = geoData.features.filter((_, i) => dists[i] <= cutoff);

    // Safety: se por algum motivo cortou demais, não aplica
    if (kept.length < Math.ceil(geoData.features.length * 0.65)) return geoData;

    return { type: "FeatureCollection", features: kept };
  }, [geoData]);


  // ------------------------------
  // ✅ Projeção perfeita (fitExtent) usando o box real
  // ------------------------------
  const projection = useMemo(() => {
    if (!geoData || !mapW || !mapH) return null;

    const pad = Math.round(Math.min(mapW, mapH) * 0.06);

    // ✅ aqui: usa o geo filtrado só pro FIT
    const fitTarget = fitGeoForProjection || geoData;

    return geoMercator().fitExtent(
      [[pad, pad], [mapW - pad, mapH - pad]],
      fitTarget
    );
  }, [geoData, mapW, mapH, fitGeoForProjection]);


  // ------------------------------
  // Data helpers
  // ------------------------------
  const getMunicipalityData = useCallback(
    (geoId) => {
      if (!geoId) return null;
      const geoIdStr = geoId.toString();

      let match = municipalitiesData.find((m) => m.codigo === geoIdStr);
      if (match) return match;

      match = municipalitiesData.find((m) => {
        const munCode = m.codigo?.toString?.() || "";
        if (!munCode) return false;
        if (geoIdStr.endsWith(munCode) || munCode.endsWith(geoIdStr)) return true;
        if (geoIdStr.length >= 6 && munCode.length >= 6) return geoIdStr.slice(-6) === munCode.slice(-6);
        return false;
      });
      if (match) return match;

      if (geoIdStr.length >= 5) {
        const last5 = geoIdStr.slice(-5);
        match = municipalitiesData.find((m) => {
          const munCode = m.codigo?.toString?.() || "";
          return munCode === last5 || munCode.endsWith(last5) || last5.endsWith(munCode);
        });
        if (match) return match;
      }

      return null;
    },
    [municipalitiesData]
  );

  const getMunicipalityName = useCallback(
    (codigo) => {
      if (!geoData) return null;
      const f = geoData.features?.find((x) => {
        const geoId = x?.properties?.id?.toString?.() || "";
        return geoId === codigo || geoId.endsWith(codigo) || codigo.endsWith(geoId);
      });
      return f?.properties?.name || f?.properties?.description || null;
    },
    [geoData]
  );

  // Top 5
  const topMunicipalities = useMemo(() => {
    if (!municipalitiesData?.length) return [];
    const sorted = [...municipalitiesData];

    if (viewMode === "export") sorted.sort((a, b) => (parseFloat(b.export) || 0) - (parseFloat(a.export) || 0));
    else if (viewMode === "import") sorted.sort((a, b) => (parseFloat(b.import) || 0) - (parseFloat(a.import) || 0));
    else sorted.sort((a, b) => Math.abs(parseFloat(b.balance) || 0) - Math.abs(parseFloat(a.balance) || 0));

    return sorted
      .slice(0, 5)
      .map((mun) => ({
        ...mun,
        name: getMunicipalityName(mun.codigo) || `Código ${mun.codigo}`,
        export: parseFloat(mun.export) || 0,
        import: parseFloat(mun.import) || 0,
        balance: parseFloat(mun.balance) || 0,
      }))
      .filter((mun) => {
        if (viewMode === "export") return mun.export > 0;
        if (viewMode === "import") return mun.import > 0;
        return true;
      });
  }, [municipalitiesData, viewMode, getMunicipalityName]);

  const filteredTopMunicipalities = useMemo(() => {
    if (!searchTerm) return topMunicipalities;
    const term = searchTerm.toLowerCase();
    return topMunicipalities.filter(
      (mun) => mun.name?.toLowerCase?.().includes(term) || mun.codigo?.toString?.().includes(term)
    );
  }, [topMunicipalities, searchTerm]);

  // Legend ticks
  const legendTicks = useMemo(() => {
    const ticks = [];
    const num = isMobile ? 5 : 7;
    for (let i = 0; i <= num; i++) ticks.push(minValue + (maxValue - minValue) * (i / num));
    return ticks;
  }, [minValue, maxValue, isMobile]);

  // ------------------------------
  // Tooltip / Products
  // ------------------------------
  const fetchProducts = useCallback(async (municipalityCode) => {
    const cacheKey = `${year}-${stateUF}-${municipalityCode}`;
    if (products[cacheKey]) return products[cacheKey];

    try {
      const res = await axios.get(`/comex/municipality-products/${year}/${stateUF}/${municipalityCode}`);
      if (res.data?.success) {
        setProducts((prev) => ({ ...prev, [cacheKey]: res.data.data }));
        return res.data.data;
      }
    } catch (e) {
      console.error("Erro ao carregar produtos do município", e);
    }
    return null;
  }, [products, year, stateUF]);

  const updateTooltipPos = useCallback((clientX, clientY) => {
    const el = mapViewportRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();

    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const tooltipWidth = isMobile ? Math.min(280, window.innerWidth * 0.9) : 350;
    const tooltipHeight = 300;

    let adjustedX = x;
    let adjustedY = y;

    if (x + tooltipWidth / 2 > rect.width) adjustedX = rect.width - tooltipWidth / 2 - 10;
    else if (x - tooltipWidth / 2 < 0) adjustedX = tooltipWidth / 2 + 10;

    if (y - tooltipHeight < 0) adjustedY = tooltipHeight + 20;
    else if (y + 20 > rect.height) adjustedY = rect.height - 20;

    setTooltipPosition({ x: adjustedX, y: adjustedY });
  }, [isMobile]);

  const handleMouseMove = useCallback((e) => {
    if (!tooltip) return;
    updateTooltipPos(e.clientX, e.clientY);
  }, [tooltip, updateTooltipPos]);

  const handleMouseEnter = useCallback(async (geo, municipalityInfo, e) => {
    setHoveredMunicipality(geo.rsmKey);

    const geoId = String(geo.properties?.id || '');
    const geoName = geo.properties?.name || geo.properties?.description || "Município";
    const pop = popMap[geoId];

    if (viewMode === "population") {
      setTooltip({
        name: geoName,
        codigo: geoId,
        populacao: pop?.populacao || 0,
        mode: "population",
      });
      if (e?.clientX != null && e?.clientY != null) updateTooltipPos(e.clientX, e.clientY);
      return;
    }

    if (viewMode === "pib") {
      const pib = pibMunMap[geoId];
      setTooltip({
        name: geoName,
        codigo: geoId,
        pibTotal: pib?.pibTotal || 0,
        pibPerCapita: pib?.pibPerCapita || 0,
        populacao: pop?.populacao || 0,
        vabAgro: pib?.vabAgro || 0,
        vabIndustria: pib?.vabIndustria || 0,
        vabServicos: pib?.vabServicos || 0,
        mode: "pib",
      });
      if (e?.clientX != null && e?.clientY != null) updateTooltipPos(e.clientX, e.clientY);
      return;
    }

    if (!municipalityInfo) return;

    const municipalityCode = municipalityInfo.codigo;
    const cacheKey = `${year}-${stateUF}-${municipalityCode}`;
    const cachedProducts = products[cacheKey];

    setTooltip({
      name: geoName,
      codigo: municipalityCode,
      export: parseFloat(municipalityInfo.export) || 0,
      import: parseFloat(municipalityInfo.import) || 0,
      balance: parseFloat(municipalityInfo.balance) || 0,
      populacao: pop?.populacao || null,
      products: cachedProducts || null,
      loadingProducts: !cachedProducts,
      mode: "trade",
    });

    if (e?.clientX != null && e?.clientY != null) updateTooltipPos(e.clientX, e.clientY);

    if (!cachedProducts) {
      const p = await fetchProducts(municipalityCode);
      setTooltip((prev) =>
        prev ? { ...prev, products: p || null, loadingProducts: false } : prev
      );
    }
  }, [fetchProducts, pibMunMap, popMap, products, stateUF, updateTooltipPos, viewMode, year]);

  const handleMouseLeave = useCallback(() => {
    if (!selectedMunicipality) {
      setHoveredMunicipality(null);
      setTooltip(null);
    }
  }, [selectedMunicipality]);

  const handleClick = useCallback((geo, municipalityInfo) => {
    const geoId = geo.properties?.id || geo.properties?.codarea || geo.properties?.CD_MUN || "";
    const name = geo.properties?.name || geo.properties?.description || "Município";

    if (municipalityInfo) {
      setSelectedMunicipality({
        name,
        codigo: municipalityInfo.codigo,
        export: parseFloat(municipalityInfo.export) || 0,
        import: parseFloat(municipalityInfo.import) || 0,
        balance: parseFloat(municipalityInfo.balance) || 0,
      });
    }

    const code = geoId || municipalityInfo?.codigo || "";
    if (code) {
      navigate(`/municipio/${code}`);
    }
  }, [navigate]);

  // ------------------------------
  // Render guards
  // ------------------------------
  if (loading || !geoData) {
    return (
      <div style={containerStyle(isMobile)}>
        <div style={{ textAlign: "center", padding: "40px" }}>
          {loading ? "📊 Carregando mapa..." : "❌ Erro ao carregar mapa do estado"}
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle(isMobile)}>
      {/* KPIs */}
      <div style={kpisContainerStyle(isMobile)}>
        <div style={kpiCardStyle(isMobile, "#16a34a")}>
          <div style={kpiLabelStyle(isMobile)}>Exportação Total</div>
          <div style={kpiValueStyle(isMobile)}>{formatUSDBR(kpis.totalExport)}</div>
        </div>
        <div style={kpiCardStyle(isMobile, "#f59e0b")}>
          <div style={kpiLabelStyle(isMobile)}>Importação Total</div>
          <div style={kpiValueStyle(isMobile)}>{formatUSDBR(kpis.totalImport)}</div>
        </div>
        <div style={kpiCardStyle(isMobile, "#2563eb")}>
          <div style={kpiLabelStyle(isMobile)}>Saldo Comercial</div>
          <div style={kpiValueStyle(isMobile)}>{formatUSDBR(kpis.totalBalance)}</div>
        </div>
        <div style={kpiCardStyle(isMobile, "#64748b")}>
          <div style={kpiLabelStyle(isMobile)}>Municípios</div>
          <div style={kpiValueStyle(isMobile)}>{kpis.municipalitiesCount}</div>
        </div>
      </div>

      {/* Header + Toggle */}
      <div style={headerStyle(isMobile)}>
        <h3 style={titleStyle(isMobile)}>
          Municípios de {stateUF} — {year}
        </h3>

        <div style={toggleContainerStyle()}>
          <button style={toggleButtonStyle(isMobile, viewMode === "export")} onClick={() => setViewMode("export")}>
            Exportação
          </button>
          <button style={toggleButtonStyle(isMobile, viewMode === "import")} onClick={() => setViewMode("import")}>
            Importação
          </button>
          <button style={toggleButtonStyle(isMobile, viewMode === "balance")} onClick={() => setViewMode("balance")}>
            Saldo
          </button>
          {popData.length > 0 && (
            <button
              style={{
                ...toggleButtonStyle(isMobile, viewMode === "population"),
                ...(viewMode === "population" ? { background: "#7c3aed", color: "#fff" } : {}),
              }}
              onClick={() => setViewMode("population")}
            >
              👥 Pop.
            </button>
          )}
          {pibData.length > 0 && (
            <button
              style={{
                ...toggleButtonStyle(isMobile, viewMode === "pib"),
                ...(viewMode === "pib" ? { background: "#059669", color: "#fff" } : {}),
              }}
              onClick={() => setViewMode("pib")}
            >
              📊 PIB
            </button>
          )}
        </div>
      </div>

      {/* Grid */}
      <div style={gridContainerStyle(isMobile)}>
        {/* MAPA */}
        <div style={mapColumnStyle()}>
          <div ref={mapStageRef} style={mapStageStyle}>
            <div
              ref={mapViewportRef}
              style={{
                ...mapContainerStyle(),
                width: `${mapW}px`,
                height: `${mapH}px`,
              }}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              <ComposableMap
                projection={projection || "geoMercator"}
                width={mapW}
                height={mapH}
                style={{ width: "100%", height: "100%" }}
              >
                <Geographies geography={geoData}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const municipalityInfo = getMunicipalityData(geo.properties?.id);
                      const geoId = String(geo.properties?.id || '');
                      let value = null;

                      if (municipalityInfo) {
                        value =
                          viewMode === "export"
                            ? parseFloat(municipalityInfo.export) || 0
                            : viewMode === "import"
                              ? parseFloat(municipalityInfo.import) || 0
                              : parseFloat(municipalityInfo.balance) || 0;
                      }

                      const isHovered = hoveredMunicipality === geo.rsmKey;
                      const isSelected =
                        selectedMunicipality?.codigo && municipalityInfo?.codigo === selectedMunicipality.codigo;

                      let fillColor;
                      if (viewMode === "population") {
                        const pop = popMap[geoId];
                        fillColor = pop && popScale ? popScale(pop.populacao) : "#e2e8f0";
                      } else if (viewMode === "pib") {
                        const pib = pibMunMap[geoId];
                        fillColor = pib && pibScale ? pibScale(pib.pibTotal) : "#e2e8f0";
                      } else {
                        const hasData = municipalityInfo && value !== 0;
                        fillColor = hasData ? colorScale(value) : "#d1d5db";
                      }

                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={fillColor}
                          stroke={isSelected ? "#0f172a" : isHovered ? "#334155" : "#9aa3af"}
                          strokeWidth={isSelected ? 2.2 : isHovered ? 1.6 : 0.9}
                          style={{
                            default: { outline: "none", transition: "all 120ms ease" },
                            hover: { outline: "none", cursor: "pointer" },
                            pressed: { outline: "none" },
                          }}
                          onMouseEnter={(e) => handleMouseEnter(geo, municipalityInfo, e)}
                          onMouseLeave={handleMouseLeave}
                          onClick={() => handleClick(geo, municipalityInfo)}
                        />
                      );
                    })
                  }
                </Geographies>
              </ComposableMap>

              {/* Tooltip */}
              {tooltip && (
                <div
                  style={{
                    ...tooltipStyle(isMobile),
                    left: `${tooltipPosition.x}px`,
                    top: `${tooltipPosition.y}px`,
                    transform: isMobile
                      ? "translate(-50%, calc(-100% - 8px))"
                      : "translate(-50%, calc(-100% - 12px))",
                  }}
                >
                  <div style={tooltipHeaderStyle(isMobile)}>
                    <strong>{tooltip.name}</strong>
                    {tooltip.codigo ? (
                      <span style={{ fontSize: "0.85rem", color: "#64748b", marginLeft: 8 }}>
                        ({tooltip.codigo})
                      </span>
                    ) : null}
                  </div>

                  <div style={tooltipContentStyle(isMobile)}>
                    {tooltip.mode === "pib" ? (
                      <>
                        <div style={tooltipRowStyle(isMobile)}>
                          <span style={{ color: "#059669" }}>📊 PIB:</span>
                          <strong>{formatBRL(tooltip.pibTotal)}</strong>
                        </div>
                        {tooltip.populacao > 0 && (
                          <div style={tooltipRowStyle(isMobile)}>
                            <span style={{ color: "#7f8c8d" }}>👥 Pop.:</span>
                            <strong>{formatPop(tooltip.populacao)}</strong>
                          </div>
                        )}
                        {tooltip.pibPerCapita > 0 && (
                          <div style={{ ...tooltipRowStyle(isMobile), borderTop: "1px solid rgba(15,23,42,.08)", paddingTop: 6, marginTop: 6 }}>
                            <span>Per capita:</span>
                            <strong style={{ color: "#059669" }}>R$ {(tooltip.pibPerCapita * 1000).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}</strong>
                          </div>
                        )}
                        {(tooltip.vabAgro > 0 || tooltip.vabIndustria > 0 || tooltip.vabServicos > 0) && (
                          <div style={{ borderTop: "1px solid rgba(15,23,42,.08)", paddingTop: 6, marginTop: 6, fontSize: "0.75rem" }}>
                            {tooltip.vabAgro > 0 && <div style={tooltipRowStyle(isMobile)}><span>🌾 Agro:</span><span>{formatBRL(tooltip.vabAgro)}</span></div>}
                            {tooltip.vabIndustria > 0 && <div style={tooltipRowStyle(isMobile)}><span>🏭 Indústria:</span><span>{formatBRL(tooltip.vabIndustria)}</span></div>}
                            {tooltip.vabServicos > 0 && <div style={tooltipRowStyle(isMobile)}><span>🏢 Serviços:</span><span>{formatBRL(tooltip.vabServicos)}</span></div>}
                          </div>
                        )}
                        <div style={{ borderTop: "1px solid rgba(15,23,42,.08)", marginTop: 8, paddingTop: 8, textAlign: "center", fontSize: "0.7rem", color: "#059669", fontWeight: 600 }}>
                          Clique para ver detalhes
                        </div>
                      </>
                    ) : tooltip.mode === "population" ? (
                      <>
                        <div style={tooltipRowStyle(isMobile)}>
                          <span style={{ color: "#7c3aed" }}>👥 População:</span>
                          <strong>{formatPop(tooltip.populacao)}</strong>
                        </div>
                        <div style={{ borderTop: "1px solid rgba(15,23,42,.08)", marginTop: 8, paddingTop: 8, textAlign: "center", fontSize: "0.7rem", color: "#7c3aed", fontWeight: 600 }}>
                          Clique para ver detalhes
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={tooltipRowStyle(isMobile)}>
                          <span style={{ color: "#16a34a" }}>📤 {isMobile ? "Exp:" : "Exportação:"}</span>
                          <strong>{formatUSDBR(tooltip.export)}</strong>
                        </div>

                        <div style={tooltipRowStyle(isMobile)}>
                          <span style={{ color: "#f59e0b" }}>📥 {isMobile ? "Imp:" : "Importação:"}</span>
                          <strong>{formatUSDBR(tooltip.import)}</strong>
                        </div>

                        <div
                          style={{
                            ...tooltipRowStyle(isMobile),
                            borderTop: "1px solid rgba(15,23,42,.08)",
                            paddingTop: isMobile ? 6 : 8,
                            marginTop: isMobile ? 6 : 8,
                          }}
                        >
                          <span style={{ fontWeight: 800 }}>Saldo:</span>
                          <strong style={{ color: tooltip.balance >= 0 ? "#2563eb" : "#dc2626" }}>
                            {formatUSDBR(tooltip.balance)}
                          </strong>
                        </div>

                        {tooltip.populacao != null && (
                          <div style={{ ...tooltipRowStyle(isMobile), fontSize: "0.85rem", color: "#64748b", marginTop: 4 }}>
                            <span>👥 População:</span>
                            <span style={{ fontWeight: 600 }}>{formatPop(tooltip.populacao)}</span>
                          </div>
                        )}

                        {tooltip.loadingProducts && (
                          <div style={{ marginTop: 10, fontSize: isMobile ? "0.7rem" : "0.75rem", color: "#64748b", textAlign: "center" }}>
                            Carregando produtos...
                          </div>
                        )}
                        <div style={{ borderTop: "1px solid rgba(15,23,42,.08)", marginTop: 8, paddingTop: 8, textAlign: "center", fontSize: "0.7rem", color: "#2563eb", fontWeight: 600 }}>
                          Clique para ver detalhes
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Legenda */}
          <div style={legendContainerStyle(isMobile)}>
            {viewMode === "pib" ? (
              <>
                <div style={legendLabelStyle(isMobile)}>
                  <span style={{ color: "#ecfdf5", fontWeight: 800, textShadow: "0 0 2px rgba(0,0,0,0.3)" }}>Menor</span>
                  <span style={{ margin: "0 8px" }}>⟵</span>
                  <span style={{ fontWeight: 900, color: "#059669" }}>📊 PIB (R$ mil)</span>
                  <span style={{ margin: "0 8px" }}>⟶</span>
                  <span style={{ color: "#064e3b", fontWeight: 800 }}>Maior</span>
                </div>
                <div style={legendWrapperStyle()}>
                  <div style={{ ...legendBarGradientStyle(isMobile), background: "linear-gradient(to right, #ecfdf5, #34d399, #059669, #064e3b)" }} />
                  <div style={legendTicksContainerStyle}>
                    {[0, 0.25, 0.5, 0.75, 1].map((frac, i) => (
                      <div key={i} style={legendTickContainerStyle}>
                        <div style={legendTickMarkerStyle} />
                        <div style={legendValueStyle(isMobile)}>{formatBRL(Math.round(pibMaxVal * frac))}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : viewMode === "population" ? (
              <>
                <div style={legendLabelStyle(isMobile)}>
                  <span style={{ color: "#faf5ff", fontWeight: 800, textShadow: "0 0 2px rgba(0,0,0,0.3)" }}>Menor</span>
                  <span style={{ margin: "0 8px" }}>⟵</span>
                  <span style={{ fontWeight: 900, color: "#7c3aed" }}>👥 População</span>
                  <span style={{ margin: "0 8px" }}>⟶</span>
                  <span style={{ color: "#4c1d95", fontWeight: 800 }}>Maior</span>
                </div>
                <div style={legendWrapperStyle()}>
                  <div
                    style={{
                      ...legendBarGradientStyle(isMobile),
                      background: "linear-gradient(to right, #faf5ff, #c084fc, #7c3aed, #4c1d95)",
                    }}
                  />
                  <div style={legendTicksContainerStyle}>
                    {[0, 0.25, 0.5, 0.75, 1].map((frac, i) => (
                      <div key={i} style={legendTickContainerStyle}>
                        <div style={legendTickMarkerStyle} />
                        <div style={legendValueStyle(isMobile)}>
                          {formatPop(Math.round(popMaxVal * frac))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div style={legendLabelStyle(isMobile)}>
                  {viewMode === "balance" ? (
                    <>
                      <span style={{ color: "#dc2626", fontWeight: 800 }}>Saldo Negativo</span>
                      <span style={{ margin: "0 8px" }}>⟵</span>
                      <span style={{ fontWeight: 900 }}>0</span>
                      <span style={{ margin: "0 8px" }}>⟶</span>
                      <span style={{ color: "#2563eb", fontWeight: 800 }}>Saldo Positivo</span>
                    </>
                  ) : (
                    <>
                      <span style={{ fontWeight: 800 }}>Menor</span>
                      <span style={{ margin: "0 8px" }}>⟵</span>
                      <span style={{ margin: "0 8px" }}>⟶</span>
                      <span style={{ fontWeight: 800 }}>Maior</span>
                    </>
                  )}
                </div>

                <div style={legendWrapperStyle()}>
                  <div
                    style={{
                      ...legendBarGradientStyle(isMobile),
                      background:
                        viewMode === "balance"
                          ? `linear-gradient(to right, ${colorScale(minValue)}, ${colorScale(0)}, ${colorScale(maxValue)})`
                          : `linear-gradient(to right, ${colorScale(minValue)}, ${colorScale(maxValue)})`,
                    }}
                  />

                  <div style={legendTicksContainerStyle}>
                    {legendTicks.map((tick, i) => (
                      <div key={i} style={legendTickContainerStyle}>
                        <div style={legendTickMarkerStyle} />
                        <div style={legendValueStyle(isMobile)}>{formatUSDBR(tick)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div style={legendNoteStyle}>
              <span style={{ color: "#d1d5db" }}>■</span> Sem dados disponíveis |{" "}
              <span style={{ marginLeft: 8 }}>US$ FOB</span>
            </div>
          </div>
        </div>

        {/* PAINEL */}
        <div style={panelColumnStyle()}>
          <div style={searchContainerStyle()}>
            <input
              type="text"
              placeholder="Buscar município..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={searchInputStyle(isMobile)}
            />
          </div>

          <div style={topMunicipalitiesContainerStyle(isMobile)}>
            <h4 style={topMunicipalitiesTitleStyle(isMobile)}>
              Top 5 {viewMode === "export" ? "Exportação" : viewMode === "import" ? "Importação" : "Saldo"}
            </h4>

            <div style={topMunicipalitiesListStyle()}>
              {filteredTopMunicipalities.length ? (
                filteredTopMunicipalities.map((mun, idx) => (
                  <div
                    key={`${mun.codigo}-${idx}`}
                    style={topMunicipalityItemStyle(isMobile, selectedMunicipality?.codigo === mun.codigo)}
                    onClick={() => setSelectedMunicipality(mun)}
                  >
                    <div style={topMunicipalityRankStyle(isMobile)}>{idx + 1}</div>

                    <div style={topMunicipalityInfoStyle()}>
                      <div style={{ minWidth: 0 }}>
                        <div style={topMunicipalityNameStyle(isMobile)}>{mun.name}</div>
                      </div>

                      <div
                        style={{
                          ...topMunicipalityValueStyle(isMobile),
                          color:
                            viewMode === "balance"
                              ? mun.balance < 0
                                ? "#dc2626"
                                : "#2563eb"
                              : "#16a34a",
                        }}
                      >
                        {viewMode === "export" && formatUSDBR(mun.export)}
                        {viewMode === "import" && formatUSDBR(mun.import)}
                        {viewMode === "balance" && formatUSDBR(mun.balance)}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ padding: 20, textAlign: "center", color: "#64748b" }}>
                  Nenhum município encontrado
                </div>
              )}
            </div>
          </div>

          {selectedMunicipality && (
            <div style={detailsContainerStyle()}>
              <h4 style={detailsTitleStyle(isMobile)}>Detalhes</h4>

              <div style={detailsContentStyle(isMobile)}>
                <div style={detailsRowStyle()}>
                  <strong>{selectedMunicipality.name}</strong>
                </div>

                <div style={detailsRowStyle()}>
                  <span>Código:</span>
                  <span>{selectedMunicipality.codigo}</span>
                </div>

                <div style={detailsRowStyle()}>
                  <span style={{ color: "#16a34a" }}>📤 Exportação:</span>
                  <strong>{formatUSDBR(selectedMunicipality.export)}</strong>
                </div>

                <div style={detailsRowStyle()}>
                  <span style={{ color: "#f59e0b" }}>📥 Importação:</span>
                  <strong>{formatUSDBR(selectedMunicipality.import)}</strong>
                </div>

                <div style={{ ...detailsRowStyle(), borderTop: "1px solid rgba(15,23,42,.08)", paddingTop: 12, marginTop: 12 }}>
                  <span style={{ fontWeight: 900 }}>Saldo:</span>
                  <strong style={{ color: selectedMunicipality.balance >= 0 ? "#2563eb" : "#dc2626", fontSize: "1.1rem" }}>
                    {formatUSDBR(selectedMunicipality.balance)}
                  </strong>
                </div>

                <button style={closeButtonStyle(isMobile)} onClick={() => setSelectedMunicipality(null)}>
                  ✕ Fechar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ------------------------------
// Styles
// ------------------------------
const containerStyle = (isMobile) => ({
  background: "#fff",
  padding: isMobile ? 12 : 16,
  borderRadius: 14,
  boxShadow: "0 6px 18px rgba(15,23,42,.06)",
  marginBottom: isMobile ? 12 : 20,
});

const kpisContainerStyle = (isMobile) => ({
  display: "grid",
  gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
  gap: isMobile ? 8 : 12,
  marginBottom: isMobile ? 12 : 16,
});

const kpiCardStyle = (isMobile, color) => ({
  background: color,
  color: "white",
  padding: isMobile ? 12 : 16,
  borderRadius: 12,
  boxShadow: "0 8px 18px rgba(0,0,0,.10)",
});

const kpiLabelStyle = (isMobile) => ({
  fontSize: isMobile ? "0.7rem" : "0.75rem",
  opacity: 0.95,
  marginBottom: 6,
  textTransform: "uppercase",
  letterSpacing: "0.6px",
});

const kpiValueStyle = (isMobile) => ({
  fontSize: isMobile ? "1rem" : "1.25rem",
  fontWeight: 800,
});

const headerStyle = (isMobile) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: isMobile ? 12 : 16,
  flexWrap: isMobile ? "wrap" : "nowrap",
  gap: isMobile ? 8 : 12,
});

const titleStyle = (isMobile) => ({
  color: "#0f172a",
  margin: 0,
  fontSize: isMobile ? "0.95rem" : "1.1rem",
  fontWeight: 900,
});

const toggleContainerStyle = () => ({
  display: "flex",
  gap: 4,
  background: "#f1f5f9",
  padding: 4,
  borderRadius: 12,
  border: "1px solid rgba(15,23,42,.08)",
});

const toggleButtonStyle = (isMobile, isActive) => ({
  padding: isMobile ? "6px 12px" : "8px 14px",
  border: "none",
  borderRadius: 10,
  background: isActive ? "#2563eb" : "transparent",
  color: isActive ? "white" : "#475569",
  fontWeight: isActive ? 900 : 700,
  cursor: "pointer",
  fontSize: isMobile ? "0.78rem" : "0.86rem",
  transition: "all 160ms ease",
});

const gridContainerStyle = (isMobile) => ({
  display: "grid",
  gridTemplateColumns: isMobile ? "1fr" : "72% 28%",
  gap: isMobile ? 14 : 18,
  alignItems: "flex-start",
});

const mapColumnStyle = () => ({
  display: "flex",
  flexDirection: "column",
});

const mapStageStyle = {
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
};

const mapContainerStyle = () => ({
  position: "relative",
  background: "#fff",
  borderRadius: 16,
  overflow: "hidden",
  border: "1px solid rgba(15,23,42,.10)",
  boxShadow: "0 12px 26px rgba(15,23,42,.08)",
});

const legendContainerStyle = (isMobile) => ({
  marginTop: isMobile ? 12 : 16,
  padding: isMobile ? 12 : 16,
  background: "#f8fafc",
  borderRadius: 14,
  border: "1px solid rgba(15,23,42,.08)",
});

const legendLabelStyle = (isMobile) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: 12,
  fontSize: isMobile ? "0.75rem" : "0.85rem",
  color: "#0f172a",
  flexWrap: "wrap",
});

const legendWrapperStyle = () => ({
  position: "relative",
  width: "100%",
  marginBottom: 8,
});

const legendBarGradientStyle = (isMobile) => ({
  width: "100%",
  height: isMobile ? 18 : 22,
  borderRadius: 10,
  marginBottom: 8,
});

const legendTicksContainerStyle = {
  display: "flex",
  justifyContent: "space-between",
  position: "relative",
  width: "100%",
};

const legendTickContainerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  flex: 1,
};

const legendTickMarkerStyle = {
  width: 1,
  height: 8,
  background: "rgba(15,23,42,.35)",
  marginBottom: 2,
};

const legendValueStyle = (isMobile) => ({
  fontSize: isMobile ? "0.65rem" : "0.7rem",
  color: "#64748b",
  marginTop: 4,
  textAlign: "center",
});

const legendNoteStyle = {
  textAlign: "center",
  fontSize: "0.75rem",
  color: "#64748b",
  fontStyle: "italic",
  marginTop: 8,
};

const panelColumnStyle = () => ({
  display: "flex",
  flexDirection: "column",
  gap: 14,
});

const searchContainerStyle = () => ({ width: "100%" });

const searchInputStyle = (isMobile) => ({
  width: "100%",
  padding: isMobile ? 10 : 12,
  border: "1px solid rgba(15,23,42,.10)",
  borderRadius: 12,
  fontSize: isMobile ? "0.85rem" : "0.9rem",
  outline: "none",
  background: "#fff",
});

const topMunicipalitiesContainerStyle = (isMobile) => ({
  padding: isMobile ? 12 : 16,
  background: "#f8fafc",
  borderRadius: 14,
  border: "1px solid rgba(15,23,42,.08)",
});

const topMunicipalitiesTitleStyle = (isMobile) => ({
  margin: "0 0 12px 0",
  fontSize: isMobile ? "0.9rem" : "0.98rem",
  fontWeight: 900,
  color: "#0f172a",
});

const topMunicipalitiesListStyle = () => ({
  display: "flex",
  flexDirection: "column",
  gap: 10,
});

const topMunicipalityItemStyle = (isMobile, isSelected) => ({
  display: "flex",
  alignItems: "center",
  gap: isMobile ? 10 : 12,
  padding: isMobile ? 10 : 12,
  background: isSelected ? "rgba(37,99,235,.08)" : "#fff",
  borderRadius: 14,
  boxShadow: isSelected ? "0 10px 22px rgba(37,99,235,.12)" : "0 4px 10px rgba(15,23,42,.05)",
  cursor: "pointer",
  border: isSelected ? "1px solid rgba(37,99,235,.35)" : "1px solid rgba(15,23,42,.06)",
  transition: "all 160ms ease",
});

const topMunicipalityRankStyle = (isMobile) => ({
  width: isMobile ? 26 : 30,
  height: isMobile ? 26 : 30,
  borderRadius: 999,
  background: "#2563eb",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: isMobile ? "0.78rem" : "0.86rem",
  fontWeight: 900,
  flexShrink: 0,
});

const topMunicipalityInfoStyle = () => ({
  flex: 1,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  minWidth: 0,
});

const topMunicipalityNameStyle = (isMobile) => ({
  fontSize: isMobile ? "0.85rem" : "0.92rem",
  fontWeight: 900,
  color: "#0f172a",
  marginBottom: 2,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const topMunicipalityValueStyle = (isMobile) => ({
  fontSize: isMobile ? "0.8rem" : "0.88rem",
  fontWeight: 900,
  whiteSpace: "nowrap",
});

const detailsContainerStyle = () => ({
  padding: 16,
  background: "#fff",
  borderRadius: 14,
  border: "1px solid rgba(37,99,235,.28)",
  boxShadow: "0 10px 22px rgba(15,23,42,.06)",
});

const detailsTitleStyle = (isMobile) => ({
  margin: "0 0 12px 0",
  fontSize: isMobile ? "0.9rem" : "0.98rem",
  fontWeight: 900,
  color: "#0f172a",
});

const detailsContentStyle = (isMobile) => ({
  fontSize: isMobile ? "0.85rem" : "0.9rem",
});

const detailsRowStyle = () => ({
  display: "flex",
  justifyContent: "space-between",
  marginBottom: 10,
  gap: 12,
});

const closeButtonStyle = (isMobile) => ({
  width: "100%",
  padding: isMobile ? 10 : 10,
  marginTop: 12,
  background: "#ef4444",
  color: "white",
  border: "none",
  borderRadius: 12,
  cursor: "pointer",
  fontSize: isMobile ? "0.85rem" : "0.9rem",
  fontWeight: 900,
});

const tooltipStyle = (isMobile) => ({
  position: "absolute",
  background: "#fff",
  padding: isMobile ? 12 : "14px 16px",
  borderRadius: 14,
  boxShadow: "0 18px 34px rgba(15,23,42,.18)",
  zIndex: 1000,
  pointerEvents: "none",
  minWidth: isMobile ? 200 : 320,
  maxWidth: isMobile ? "90vw" : 380,
  border: "1px solid rgba(15,23,42,.12)",
  maxHeight: isMobile ? "70vh" : 520,
  overflowY: "auto",
});

const tooltipHeaderStyle = (isMobile) => ({
  fontSize: isMobile ? "0.95rem" : "1rem",
  color: "#0f172a",
  marginBottom: isMobile ? 10 : 12,
  paddingBottom: isMobile ? 8 : 10,
  borderBottom: "1px solid rgba(15,23,42,.08)",
  fontWeight: 900,
});

const tooltipContentStyle = (isMobile) => ({
  fontSize: isMobile ? "0.82rem" : "0.9rem",
  color: "#0f172a",
});

const tooltipRowStyle = (isMobile) => ({
  display: "flex",
  justifyContent: "space-between",
  marginBottom: isMobile ? 6 : 8,
  gap: 12,
});

export default StateMunicipalitiesMap;
